import { Getter } from '../Getter';
import { HttpError } from '../HttpError';
import { ApiMetric, MetricName, Days } from './Models/ApiMetric';
import { Metric } from './Models/Metric';
import isValidUUID from 'uuid-validate';
import dayjs from 'dayjs';
import URI from 'urijs';

type ApiResponse = ApiMetric | null | undefined;

interface DataResponse {
  data?: Metric;
  error?: never;
}
interface ErrorResponse {
  data?: never;
  error: HttpError;
}
export type ClientOutput = DataResponse | ErrorResponse;

export interface ClientInput {
  organizationUUID: string;
  metric: MetricName;
  period: Days;
  byReviewer?: boolean;
  userName?: string;
  repositoryUUID?: string;
}

export class MetricClient {
  private getter: Getter<ApiResponse | HttpError>;

  constructor(getter: Getter<ApiResponse | HttpError>) {
    this.getter = getter;
  }

  public async retrieveAsync({
    organizationUUID,
    metric,
    period,
    byReviewer,
    userName,
    repositoryUUID,
  }: ClientInput): Promise<ClientOutput> {
    if (
      !isValidUUID(organizationUUID) ||
      (repositoryUUID && !isValidUUID(repositoryUUID))
    ) {
      // equivalent to the response we'd get on the server
      return {
        error: new HttpError(400),
      };
    }

    const response = await this.tryRetrieveAsync({
      organizationUUID,
      metric,
      period,
      byReviewer,
      userName,
      repositoryUUID,
    });

    if (!response) {
      return {
        data: undefined,
        error: undefined,
      };
    }

    if (response instanceof HttpError) {
      return {
        error: response,
      };
    }

    return { data: this.toMetric(response) };
  }

  private toMetric(response: ApiMetric): Metric {
    const { period, by_day, overall } = response;

    return {
      period: {
        type: period.type,
        from: new Date(period.from),
        value: period.value,
      },
      byDay: by_day,
      overall: {
        averageCommentsPerReview: overall.average_comments_per_review,
        averageDefects: overall.average_defects,
        averageInlineComments: overall.average_inline_comments,
        averageLineCountsPerReview: overall.average_line_counts_per_review,
        averageReviewLifecycleHours: overall.average_review_lifecycle_hours,
        averageReviewerLatencyHours: overall.average_reviewer_latency_hours,
        averageTestLineCountsPerReview:
          overall.average_test_line_counts_per_review ?? undefined,
        defectRate: overall.defect_rate,
        percentTestLinesPerReview:
          overall.percent_test_lines_per_review ?? undefined,
        totalAdditions: overall.total_additions ?? undefined,
        totalCodeReviewsCompleted: overall.total_code_reviews_completed,
        totalComments: overall.total_comments,
        totalDefects: overall.total_defects,
        totalDeletions: overall.total_deletions ?? undefined,
        totalInlineComments: overall.total_inline_comments,
        totalLineCounts: overall.total_line_counts,
        totalTestLineCounts: overall.total_test_line_counts ?? undefined,
      },
    };
  }

  private async tryRetrieveAsync({
    organizationUUID,
    metric,
    period,
    byReviewer,
    userName,
    repositoryUUID,
  }: ClientInput): Promise<ApiResponse | HttpError> {
    const href = this.getHref({
      organizationUUID,
      metric,
      period,
      byReviewer,
      userName,
      repositoryUUID,
    });

    return await this.getter.get(href);
  }

  public getHref({
    organizationUUID,
    metric,
    period,
    byReviewer,
    userName,
    repositoryUUID,
  }: ClientInput): string {
    const href = URI('/api/v1/organizations/')
      .segmentCoded(organizationUUID)
      .segment('benchmark-metrics')
      .addQuery({
        metric,
        days: period.toString(),
        timezone: dayjs().format('Z'),
      });

    if (byReviewer) {
      href.addQuery({ by_reviewer: '1' });
    }

    if (userName) {
      href.addQuery({ user: userName });
    }

    if (repositoryUUID) {
      href.addQuery({ repository: repositoryUUID });
    }

    return href.toString();
  }
}
